'use client';

import React, { useEffect, useState } from 'react';
import { Book, Plus, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import Link from 'next/link';
import { useUser } from '@clerk/nextjs';

const ADMIN_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";

export default function CodeHub() {
    const { user } = useUser();
    const [policies, setPolicies] = useState<any[]>([]);
    const [chapters, setChapters] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [newChapterName, setNewChapterName] = useState('');
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const [titles, setTitles] = useState<any[]>([]);
    const [newTitleName, setNewTitleName] = useState('');
    
    const divisions = ["Paradise", "The World", "Government", "Man"];

    const [isAdmin, setIsAdmin] = useState(false);

    const isEligibleAdmin = user?.id === ADMIN_ID;

    const fetchData = async () => {
        try {
            const [billsRes, chaptersRes, titlesRes] = await Promise.all([
                fetch('/api/bills/effective/list', { cache: 'no-store' }),
                fetch('/api/bills/chapters/full', { cache: 'no-store' }),
                fetch('/api/bills/titles/list', { cache: 'no-store' })
            ]);

            const billsData = await billsRes.json();
            const chaptersData = await chaptersRes.json();
            const titlesData = await titlesRes.json();

            if (Array.isArray(billsData)) {
                const sorted = [...billsData].sort((a, b) => {
                    if (a.position != null && b.position != null) {
                        return a.position - b.position;
                    }
                    if (a.position != null) return -1;
                    if (b.position != null) return 1;

                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                });
                setPolicies(sorted);
            }
            if (Array.isArray(chaptersData)) setChapters(chaptersData);

            if (Array.isArray(titlesData)) {
                const sortedTitles = [...titlesData].sort((a, b) => (a.position || 0) - (b.position || 0));
                setTitles(sortedTitles);
            }

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const [expandedArticles, setExpandedArticles] = useState<Record<string, boolean>>({});

    const toggleArticleExpansion = async (e: React.MouseEvent, articleId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanding = !expandedArticles[articleId];
        const targetPolicy = policies.find(p => p.id === articleId);

        if (isExpanding && targetPolicy && !targetPolicy.paragraphs) {
            try {
                const response = await fetch(`/api/bills/effective/numbered/${targetPolicy.slug}`);
                const data = await response.json();

                if (data && data.paragraphs) {
                    setPolicies(prev => prev.map(p =>
                        p.id === articleId ? {
                            ...p,
                            paragraphs: data.paragraphs,
                            globalOffset: data.globalOffset,
                            isBeginning: data.isBeginning

                        } : p
                    ));
                }
            } catch (error) {
                console.error(error);
            }
        }

        setExpandedArticles(prev => ({ ...prev, [articleId]: isExpanding }));
    };

    const handleChapterDragStart = (e: React.DragEvent, chapterId: string) => {
        if (!isAdmin) return;
        e.dataTransfer.setData("chapterId", chapterId);
    };

    const handleDropOnTitle = async (e: React.DragEvent, targetTitleName: string) => {
        if (!isAdmin) return;
        e.preventDefault();
        const chapterId = e.dataTransfer.getData("chapterId");
        if (!chapterId) return;

        try {
            const response = await fetch('/api/bills/titles/update', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chapterId,
                    name: targetTitleName
                })
            });
            if (response.ok) fetchData();
        } catch (error) {
            console.error('Failed to move chapter:', error);
        }
    };

    const addChapter = async () => {
        if (!newChapterName) return;
        try {
            const response = await fetch('/api/bills/create-chapter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newChapterName })
            });
            if (response.ok) {
                setNewChapterName('');
                fetchData();
            }
        } catch (error) {
            console.error('Failed to create:', error);
        }
    };

    const toggleCollapse = (chapterId: string) => {
        setCollapsed(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
    };

    const handleDragStart = (e: React.DragEvent, billId: string) => {
        if (!isAdmin) return;
        e.dataTransfer.setData("billId", billId);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (!isAdmin) return;
        e.preventDefault();
    };

    const handleDropOnChapter = async (e: React.DragEvent, targetTagName: string) => {
        if (!isAdmin) return;
        e.preventDefault();
        e.stopPropagation();
        const billId = e.dataTransfer.getData("billId");

        const updatedPolicies = policies.map(p =>
            p.id === billId ? { ...p, tags: [targetTagName] } : p
        );

        const updates = updatedPolicies.map((p, i) => ({
            id: p.id,
            position: i,
            tags: p.tags,
            title: p.title,
            category: p.category
        }));

        try {
            const response = await fetch('/api/bills/update-position', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
            if (response.ok) fetchData();
        } catch (error) {
            console.error('Chapter update failed:', error);
        }
    };

    const handleDropOnArticle = async (e: React.DragEvent, targetPolicy: any) => {
        if (!isAdmin) return;
        e.preventDefault();
        e.stopPropagation();

        const draggedId = e.dataTransfer.getData('billId');
        if (draggedId === targetPolicy.id) return;

        const reordered = [...policies];
        const draggedIdx = reordered.findIndex(p => p.id === draggedId);
        const targetIdx = reordered.findIndex(p => p.id === targetPolicy.id);

        const [movedItem] = reordered.splice(draggedIdx, 1);
        movedItem.tags = targetPolicy.tags;
        reordered.splice(targetIdx, 0, movedItem);

        setPolicies(reordered);

        const updates = reordered.map((p, i) => ({
            id: p.id,
            position: i,
            tags: p.tags,
            title: p.title,
            category: p.category
        }));

        try {
            await fetch('/api/bills/update-position', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
            });
        } catch (error) {
            console.error(error);
            fetchData();
        };
    };

    if (loading) return <div className="min-h-screen flex items-center bg-amber-50 bg-[#fcfaf7] justify-center font-serif italic text-slate-500">Retrieving Official Records from the Archive...</div>;

    const miscPolicies = policies.filter(p => !p.tags || p.tags.length === 0 || !chapters.find(c => c.name === p.tags[0]));

    return (
        <div className="min-h-screen bg-amber-50 bg-[#fcfaf7] text-slate-900 font-serif selection:bg-amber-200">
            <div className="flex mx-auto">
                {isEligibleAdmin && !isAdmin && (
                    <button
                        onClick={() => setIsAdmin(true)}
                        className="mt-6 mr-10 -mb-10 px-4 py-2 mx-right ml-auto bg-slate-800 text-white font-sans text-xs font-bold uppercase rounded hover:bg-slate-700"
                    >
                        Enter Admin
                    </button>
                )}
                {isAdmin && (
                    <button
                        onClick={() => setIsAdmin(false)}
                        className="mt-6 ml-auto mr-10 -mb-10 px-4 py-2 bg-orange-200 text-orange-900 font-sans text-xs font-bold uppercase rounded hover:bg-orange-300"
                    >
                        Leave Admin
                    </button>
                )}
            </div>
            <div className="max-w-5xl mx-auto px-8 py-20">

                <header className="border-b-4 border-double border-slate-800 pb-5 mb-16 text-center">
                    <div className="flex justify-center mb-6">
                        <Book size={48} className="text-slate-800" />
                    </div>
                    <h1 className="text-5xl font-black tracking-tighter mb-4">Code of the Holy Empire of America</h1>
                    <p className="text-lg uppercase tracking-[0.2em] font-sans font-semibold text-slate-600 mb-2">Official Collection of Laws</p>
                    <p className="text-sm font-sans text-slate-500 mb-3" >Codified Laws - Holy Empire of American Code - HEAC </p>


                </header>

                {isAdmin && (
                    <div className="mb-6 -mt-6 p-8 bg-slate-100 border border-slate-200 shadow-sm flex gap-4 items-center">
                        <input
                            type="text"
                            placeholder="Designate New Title..."
                            value={newTitleName}
                            onChange={(e) => setNewTitleName(e.target.value)}
                            className="flex-1 p-2 border border-slate-300 font-sans outline-none focus:border-slate-800"
                        />
                        <button
                            onClick={async () => {
                                if (!newTitleName) return;
                                const res = await fetch('/api/bills/titles/create', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ name: newTitleName })
                                });
                                if (res.ok) {
                                    setNewTitleName('');
                                    fetchData();
                                }
                            }}
                            className="bg-slate-900 text-white px-6 py-2 font-sans flex items-center gap-2 hover:bg-slate-700 transition-colors"
                        >
                            <Plus size={18} /> Establish Title
                        </button>
                    </div>
                )}

                {isAdmin && (
                    <div className="mb-12 p-8 bg-amber-100 border border-slate-200 shadow-sm flex gap-4 items-center">
                        <input
                            type="text"
                            placeholder="Designate New Chapter Title..."
                            value={newChapterName}
                            onChange={(e) => setNewChapterName(e.target.value)}
                            className="flex-1 p-2 border border-slate-300 font-sans outline-none focus:border-slate-800"
                        />
                        <button
                            onClick={addChapter}
                            className="bg-slate-800 text-white px-6 py-2 font-sans flex items-center gap-2 hover:bg-slate-700 transition-colors"
                        >
                            <Plus size={18} /> Establish Chapter
                        </button>
                    </div>
                )}

                <div className="space-y-12">
                    {titles.map((title, tIdx) => {
                        let titleRunningOffset = 0;
                        const isBeginning = title.name === 'Beginning';

                        return (
                            <section
                                key={title.id}
                                className="space-y-4"
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDropOnTitle(e, title.name)}
                            >
                                <div className="border-b-4 border-double border-slate-800 mb-6 flex items-center gap-4">
                                    <button
                                        onClick={() => toggleCollapse(title.id)}
                                        className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                                    >
                                        {!collapsed[title.id] ? <ChevronRight size={24} className="text-slate-900" /> : <ChevronDown size={24} className="text-slate-800" />}
                                    </button>
                                    {!isBeginning && (
                                        <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 py-2">
                                            Title {tIdx}: {title.name}
                                        </h2>
                                    )}
                                    {isBeginning && (
                                        <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 py-2">
                                            Beginning Title
                                        </h2>
                                    )}

                                </div>

                                {!!collapsed[title.id] && (
                                    <div className="space-y-3 pl-6 border-l-2 border-slate-200">
                                        {chapters
                                            .filter(c => c.title_name === title.name)
                                            .map((chapter, idx) => {
                                                const chapterPolicies = policies.filter(p => p.tags?.[0] === chapter.name);

                                                return (
                                                    <nav
                                                        key={chapter.id}
                                                        draggable={isAdmin}
                                                        onDragStart={(e) => {
                                                            if (!isAdmin) return;
                                                            e.dataTransfer.setData("chapterId", chapter.id);
                                                        }}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDropOnChapter(e, chapter.name)}
                                                        className="p-8 bg-amber-100 border border-slate-200 shadow-sm"
                                                    >
                                                        <div className={`flex items-center justify-between border-black border-b pb-2 ${!!collapsed[chapter.id] && 'mb-6'} mb-0`}>
                                                            <div className="flex items-center gap-3 flex-1">
                                                                {isAdmin && <GripVertical size={16} className="text-slate-400 cursor-grab opacity-50 hover:opacity-100" />}
                                                                <button
                                                                    onClick={() => toggleCollapse(chapter.id)}
                                                                    className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                                                                >
                                                                    {!collapsed[chapter.id] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                                                </button>
                                                                <Link
                                                                    href={`/code/chapter/${encodeURIComponent(chapter.name)}`}
                                                                    className="hover:opacity-80 transition-opacity flex-1"
                                                                >
                                                                    <h2 className="font-sans font-bold uppercase text-sm tracking-widest">Chapter {idx + 1}: {chapter.name}</h2>
                                                                </Link>
                                                            </div>
                                                            <span className="font-sans font-bold text-xs text-slate-500 uppercase">
                                                                {chapterPolicies.length} Articles
                                                            </span>
                                                        </div>

                                                        {!!collapsed[chapter.id] && (
                                                            <ul className="space-y-3">
                                                                {chapterPolicies.map((policy, pIdx) => {

                                                                    const currentPolicyOffset = titleRunningOffset;
                                                                    const validSections = policy.paragraphs?.filter((p: any) => p.position !== 0) || [];

                                                                    if (!isBeginning) {
                                                                        titleRunningOffset += validSections.length;
                                                                    }

                                                                    return (
                                                                        <li
                                                                            key={policy.id}
                                                                            draggable={isAdmin}
                                                                            onDragStart={(e) => handleDragStart(e, policy.id)}
                                                                            onDragOver={handleDragOver}
                                                                            onDrop={(e) => handleDropOnArticle(e, policy)}
                                                                            className={`flex flex-col group ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                                                        >
                                                                            <div className='flex items-baseline gap-4'>
                                                                                {isAdmin && <GripVertical size={14} className="text-slate-400 self-center opacity-0 group-hover:opacity-100 transition-opacity" />}
                                                                                <button
                                                                                    onClick={(e) => toggleArticleExpansion(e, policy.id)}
                                                                                    className="pyq-1 translate-y-0 hover:bg-amber-200 rounded transition-colors"
                                                                                >
                                                                                    {expandedArticles[policy.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                                                </button>
                                                                                <span className="font-sans font-bold text-slate-900 w-20 shrink-0">Article {pIdx + 1}</span>

                                                                                <div className={`text-white font-sans px-2 font-bold text-[10px] py-1 rounded shrink-0 ${policy.category === 'Moral' ? 'bg-red-800' : ''}  ${policy.category === 'Introductory' ? 'bg-orange-500' : ''}  ${policy.category === 'General' ? 'bg-blue-800' : ''} ${policy.category === 'Divine' ? 'bg-purple-800' : ''} ${!['Moral', 'General', 'Divine', "Introductory"].includes(policy.category) ? 'bg-slate-700' : ''}`}>
                                                                                    {policy.category === 'General' ? 'Human' : policy.category === "Divine" ? "Eternal" : policy.category === "Moral" ? "Natural" : policy.category === "Rule" ? "Political" : policy.category === "Opinion" ? "Political" : policy.category}
                                                                                </div>

                                                                                <Link
                                                                                    onClick={(e) => { e.preventDefault(); window.open(`/code/${policy.slug}`, "popup", "width=900,height=700"); }}

                                                                                    href={`/code/${policy.slug}`}
                                                                                    className="hover:text-amber-800 hover:underline decoration-amber-500 decoration-2 transition-colors"
                                                                                >
                                                                                    {policy.title}
                                                                                </Link>
                                                                            </div>

                                                                            {expandedArticles[policy.id] && policy.paragraphs && (
                                                                                <div className="ml-4 space-y-1 pb-0 border-l-2 border-amber-200 pl-4 mt-2">
                                                                                    {policy.paragraphs
                                                                                        .filter((p: any) => p.position !== 0)
                                                                                        .map((para: any, sIdx: number) => {
                                                                                            const sectionNum = policy.isBeginning ? sIdx + 1 : (policy.globalOffset || 0) + sIdx + 1;

                                                                                            return (
                                                                                                <Link
                                                                                                    key={para.id}
                                                                                                    target='_blank'
                                                                                                    onClick={(e) => { e.preventDefault(); window.open(`/code/${policy.slug}/section/${sectionNum}`, "popup", "width=600,height=400"); }}
                                                                                                    href={`/code/${policy.slug}/section/${sectionNum}`}
                                                                                                    className="block hover:underline text-sm text-slate-600 hover:text-amber-900 transition-colors py-0.5"
                                                                                                >
                                                                                                    <span className="font-sans font-bold mr-2 text-[11px] text-amber-800">Section {sectionNum}. {para.title}</span>
                                                                                                    <span className="italic"></span>
                                                                                                </Link>
                                                                                            )
                                                                                        }
                                                                                        )}
                                                                                </div>
                                                                            )}
                                                                        </li>

                                                                    )
                                                                }
                                                                )}
                                                            </ul>
                                                        )}
                                                    </nav>
                                                );
                                            })}
                                    </div>
                                )}
                            </section>

                        )
                    }
                    )}

                    <section
                        className="space-y-4"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDropOnTitle(e, null)}
                    >
                        <div className="border-b-4 border-double border-slate-400 mb-6 flex items-center gap-4 opacity-100">
                            <button
                                onClick={() => toggleCollapse('title-misc')}
                                className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                            >
                                {!collapsed['title-misc'] ? <ChevronRight size={24} className="text-slate-900" /> : <ChevronDown size={24} className="text-slate-500" />}
                            </button>
                            <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 py-2">
                                Supplementary Title    {/* Supplementary Title Title {titles.length}: Supplement */}
                            </h2>
                        </div>

                        {!!collapsed['title-misc'] && (
                            <div className="space-y-3 pl-6 border-l-2 border-slate-200">
                                {chapters
                                    .filter(c => !c.title_name)
                                    .map((chapter, idx) => {
                                        const chapterPolicies = policies.filter(p => p.tags?.[0] === chapter.name);
                                        return (
                                            <nav
                                                key={chapter.id}
                                                draggable={isAdmin}
                                                onDragStart={(e) => {
                                                    if (!isAdmin) return;
                                                    e.dataTransfer.setData("chapterId", chapter.id);
                                                }}
                                                onDragOver={handleDragOver}
                                                onDrop={(e) => handleDropOnChapter(e, chapter.name)}
                                                className="p-8 bg-amber-100 border border-slate-200 shadow-sm"
                                            >
                                                <div className={`flex items-center justify-between border-black border-b pb-2 ${!!collapsed[chapter.id] && 'mb-6'} mb-0`}>
                                                    <div className="flex items-center gap-3 flex-1">
                                                        {isAdmin && <GripVertical size={16} className="text-slate-400 cursor-grab" />}
                                                        <button onClick={() => toggleCollapse(chapter.id)}>
                                                            {!collapsed[chapter.id] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                                        </button>
                                                        <Link href={`/code/chapter/${encodeURIComponent(chapter.name)}`} className="hover:opacity-80 transition-opacity flex-1">
                                                            <h2 className="font-sans font-bold uppercase text-sm tracking-widest">Chapter {idx + 1}: {chapter.name}</h2>
                                                        </Link>
                                                    </div>
                                                    <span className="font-sans font-bold text-xs text-slate-500 uppercase">{chapterPolicies.length} Articles</span>
                                                </div>
                                                {!!collapsed[chapter.id] && (
                                                    <ul className="space-y-3">
                                                        {chapterPolicies.map((policy, pIdx) => (
                                                            <li
                                                                key={policy.id}
                                                                draggable={isAdmin}
                                                                onDragStart={(e) => handleDragStart(e, policy.id)}
                                                                onDragOver={handleDragOver}
                                                                onDrop={(e) => handleDropOnArticle(e, policy)}
                                                                className={`flex items-baseline gap-4 group ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                                            >
                                                                {isAdmin && <GripVertical size={14} className="text-slate-400 self-center opacity-0 group-hover:opacity-100 transition-opacity" />}
                                                                <span className="font-sans font-bold text-slate-900 w-20 shrink-0">Article {pIdx + 1}</span>
                                                                <div className={`text-white font-sans px-2 font-bold text-[10px] py-1 rounded shrink-0 ${policy.category === 'Moral' ? 'bg-red-800' : ''} ${policy.category === 'Introductory' ? 'bg-orange-500' : ''} ${policy.category === 'General' ? 'bg-blue-800' : ''} ${policy.category === 'Divine' ? 'bg-purple-800' : ''} ${!['Moral', 'General', 'Divine', "Introductory"].includes(policy.category) ? 'bg-slate-700' : ''}`}>
                                                                    {policy.category === 'General' ? 'Human' : policy.category === "Divine" ? "Eternal" : policy.category === "Moral" ? "Natural" : policy.category === "Rule" ? "Political" : policy.category === "Opinion" ? "Political" : policy.category}
                                                                </div>
                                                                <Link href={`/code/${policy.slug}`} className="hover:text-amber-800 hover:underline decoration-amber-500 decoration-2 transition-colors">
                                                                    {policy.title}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </nav>
                                        );


                                    })}

                                {miscPolicies.length > 0 && (
                                    <nav
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDropOnChapter(e, '')}
                                        className="p-8 bg-amber-100 border border-slate-200 shadow-sm"
                                    >
                                        <div className={`flex items-center justify-between border-black border-b pb-2 ${!!collapsed["misc"] && 'mb-6'} mb-0`}>
                                            <div className="flex items-center gap-3 flex-1">
                                                <button
                                                    onClick={() => toggleCollapse("misc")}
                                                    className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                                                >
                                                    {!collapsed["misc"] ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                                </button>
                                                <Link
                                                    href={`/code/chapter/Miscellaneous`}
                                                    className="hover:opacity-80 transition-opacity flex-1"
                                                >
                                                    <h2 className="font-sans font-bold uppercase text-sm tracking-widest">
                                                        Chapter {chapters.filter(c => !c.title_name).length + 1}: Miscellaneous
                                                    </h2>
                                                </Link>
                                            </div>
                                            <span className="font-sans font-bold text-xs text-slate-500 uppercase">
                                                {miscPolicies.length} Articles
                                            </span>
                                        </div>
                                        {!!collapsed["misc"] && (
                                            <ul className="space-y-3 opacity-100 text-slate-900">
                                                {miscPolicies.map((policy, idx) => (
                                                    <li
                                                        key={policy.id}
                                                        draggable={isAdmin}
                                                        onDragStart={(e) => handleDragStart(e, policy.id)}
                                                        onDragOver={handleDragOver}
                                                        onDrop={(e) => handleDropOnArticle(e, policy)}
                                                        className={`flex items-baseline gap-4 group ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                                    >
                                                        {isAdmin && <GripVertical size={14} className="text-slate-400 self-center opacity-0 group-hover:opacity-100 transition-opacity" />}
                                                        <span className="font-sans font-bold text-slate-900 w-20 shrink-0">Article {idx + 1}</span>
                                                        <div className={`text-white font-sans px-2 font-bold text-[10px] py-1 rounded shrink-0 ${policy.category === 'Moral' ? 'bg-red-800' : ''} ${policy.category === 'General' ? 'bg-blue-800' : ''} ${policy.category === 'Divine' ? 'bg-purple-800' : ''} ${policy.category === 'Introductory' ? 'bg-orange-500' : ''} ${!['Moral', 'General', 'Divine'].includes(policy.category) ? 'bg-slate-700' : ''}`}>
                                                            {policy.category === 'General' ? 'Human' : policy.category === "Divine" ? "Eternal" : policy.category === "Moral" ? "Natural" : policy.category === "Rule" ? "Political" : policy.category === "Opinion" ? "Political" : policy.category}
                                                        </div>
                                                        <Link
                                                            href={`/code/${policy.slug}`}
                                                            className="hover:text-amber-800 hover:underline decoration-amber-500 decoration-2 transition-colors"
                                                        >
                                                            {policy.title}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </nav>
                                )}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}